import React from 'react';
import { getMentoringPageContent } from '../mentoringContent';
import {
  ActionLink,
  ActionTextLink,
  Card,
  CardGrid,
  CheckList,
  Facts,
  Faq,
  Hero,
  Lettered,
  Section,
  Steps,
  TagList,
} from '../components/MentoringPrimitives';
import { MentoringPageShell } from './shared';

const ProgramBody = ({ locale }) => {
  const content = getMentoringPageContent(locale, 'program');

  if (!content) {
    return null;
  }

  const {
    hero, audience, model, curriculum, mentorship, selection, commitment, after, faq,
  } = content;

  return (
    <>
      <Hero {...hero} split />

      <Section
        marker={audience.marker}
        eyebrow={audience.eyebrow}
        title={audience.title}
        lead={audience.lead}
      >
        <div className="mt-split mt-split--2">
          <div>
            <p className="mt-subhead">{audience.profilesTitle}</p>
            <CardGrid cols={1}>
              {audience.profiles.map((profile) => (
                <Card key={profile.title} title={profile.title} text={profile.text} />
              ))}
            </CardGrid>
          </div>
          <div>
            <p className="mt-subhead">{audience.needsTitle}</p>
            <CheckList items={audience.needs} />
          </div>
        </div>
      </Section>

      <Section
        marker={model.marker}
        eyebrow={model.eyebrow}
        title={model.title}
        lead={model.lead}
        tone="paper2"
      >
        <Steps items={model.steps} />
      </Section>

      <Section
        marker={curriculum.marker}
        eyebrow={curriculum.eyebrow}
        title={curriculum.title}
        lead={curriculum.lead}
        shell="mt-shell"
      >
        <div className="mt-grid mt-grid--3">
          {curriculum.groups.map((group) => (
            <div className="mt-group" key={group.title}>
              <h3 className="mt-group__title">{group.title}</h3>
              <TagList items={group.items} />
            </div>
          ))}
        </div>
      </Section>

      <Section
        marker={mentorship.marker}
        eyebrow={mentorship.eyebrow}
        title={mentorship.title}
        lead={mentorship.lead}
        tone="accent"
      >
        <Facts items={mentorship.facts} />
        <div className="mt-section__actions">
          <Lettered items={mentorship.pillars} />
        </div>
        <div className="mt-mentorship-expertise">
          <p className="mt-subhead">{mentorship.expertiseTitle}</p>
          <p className="mt-lead mt-mentorship-lead">{mentorship.expertiseLead}</p>
          <CardGrid cols={3}>
            {mentorship.expertise.map((area) => (
              <Card key={area.title} title={area.title} text={area.text} />
            ))}
          </CardGrid>
          <div className="mt-section__actions">
            <ActionLink action={mentorship.cta} variant="on-dark" />
          </div>
        </div>
      </Section>

      <Section
        marker={selection.marker}
        eyebrow={selection.eyebrow}
        title={selection.title}
        lead={selection.lead}
      >
        <Steps items={selection.steps} />
      </Section>

      <Section
        marker={commitment.marker}
        eyebrow={commitment.eyebrow}
        title={commitment.title}
        lead={commitment.lead}
        tone="ink"
      >
        <Facts items={commitment.facts} stacked />
      </Section>

      <Section
        marker={after.marker}
        eyebrow={after.eyebrow}
        title={after.title}
        lead={after.lead}
        tone="paper2"
      >
        <Steps items={after.steps} />
        <div className="mt-section__actions">
          <p className="mt-note">{after.note}</p>
        </div>
      </Section>

      <Section
        marker={faq.marker}
        eyebrow={faq.eyebrow}
        title={faq.title}
        lead={faq.lead}
        shell="mt-shell--narrow"
      >
        <Faq items={faq.items} />
        <div className="mt-section__actions">
          <ActionTextLink action={faq.link} />
        </div>
      </Section>
    </>
  );
};

export default function MentoringProgramPage({ locale }) {
  return (
    <MentoringPageShell locale={locale} pageKey="program">
      <ProgramBody locale={locale} />
    </MentoringPageShell>
  );
}
